/**
 * onboardingRoute.js — is the first-time onboarding holding this person's turns? 2026-09-26.
 *
 * WEB ONLY. A brand-new visitor's first message goes to the GPT stream; the server's
 * onboarding gate answers it and replies with `done.onboarding.active = true`. From
 * then on, until the server says the flow is over (`_onboardingDone` on a reply), the
 * app sends ordinary turns to /api/ai/conversation instead of the stream - exactly
 * what it already does while a tour beat is set. That is where the lanes live (the
 * web-search demo, the walkthrough, writing), and none of those turns spend a GPT
 * call or a guest turn.
 *
 * The flag is kept per user id in localStorage so a refresh keeps routing; the
 * server holds the real state and re-asks the pending question on "hey".
 * Pure module: no React, no network, so it can be tested under plain Node.
 */

const KEY_PREFIX = 'aria_onboarding_active:';

function currentUserId() {
  try { return String(localStorage.getItem('aria_user_id') || '').trim(); } catch (_) { return ''; }
}

function keyFor(userId) {
  return KEY_PREFIX + String(userId || currentUserId() || 'default');
}

/** True while the server has told us onboarding is in progress for this user. */
export function isOnboardingActive(userId) {
  try { return localStorage.getItem(keyFor(userId)) === '1'; } catch (_) { return false; }
}

/** Set from a stream `done.onboarding.active` or cleared by a JSON reply's `_onboardingDone`. */
export function setOnboardingActive(active, userId) {
  try {
    if (active) localStorage.setItem(keyFor(userId), '1');
    else localStorage.removeItem(keyFor(userId));
  } catch (_) { /* private mode: routing falls back to the stream, the gate still answers */ }
}

/**
 * Read the routing signal off a reply from either route.
 * Returns { active, openSignup } where `active` is null when the reply carries no signal.
 */
export function readOnboardingSignal(reply) {
  const r = reply && typeof reply === 'object' ? reply : {};
  if (r.onboarding && typeof r.onboarding === 'object') {
    return { active: r.onboarding.active === true, openSignup: r.onboarding.openSignup === true };
  }
  if (r._onboardingDone === true) return { active: false, openSignup: r._openSignup === true };
  if (r._onboarding && typeof r._onboarding === 'object' && r.source === 'aria_onboarding') {
    return { active: r._onboarding.step !== 'done', openSignup: r._openSignup === true };
  }
  return { active: null, openSignup: r._openSignup === true };
}

/** Apply a reply's signal to storage; returns the signal so the caller can open sign-up. */
export function applyOnboardingSignal(reply, userId) {
  const sig = readOnboardingSignal(reply);
  if (sig.active === true) setOnboardingActive(true, userId);
  else if (sig.active === false) setOnboardingActive(false, userId);
  return sig;
}
